import { HAS_ROLE_PERMISSION } from "../../rbac/RBAC";
import { fieldContainsValue, useHubOrRole } from "../../controllers/common";
import { useEffect, useState, useCallback, useRef } from "react";
import Cookies from "js-cookie";
import { generateSalt, decryptObject, decodeObject } from "../utils/encryption";

export const useMessaageButton = (buttonMessageConfig) => {
  const { role } = useHubOrRole();
  const buttonKeyArr = Object.keys(buttonMessageConfig) || [];
  if (buttonKeyArr.length === 0) return {};

  const result = {};
  for (const buttonKey of buttonKeyArr) {
    const buttonConfig = buttonMessageConfig[buttonKey];
    const { buttons, conditions = [] } = buttonConfig;
    // First check if user has permission for this field
    const hasPermission = HAS_ROLE_PERMISSION(role, buttons);

    // If no permission, show default no permission message
    if (!hasPermission) {
      result[buttonKey] = "You don't have permission to perform this action";
    } else {
      // If has permission, check conditions and show custom message
      for (const cond of conditions || []) {
        if (cond?.check && cond?.check()) {
          result[buttonKey] = cond?.message || "";
          break;
        }
      }
    }
  }
  return result;
};

export const useAgentAccountDetails = () => {
  const accountDetails = Cookies.get("accountDetails");
  if (!fieldContainsValue(accountDetails)) {
    return {
      agentRole: null,
      agentEmail: null,
      agentName: null,
      agentPhone: null,
      agentHub: null,
      agentAccountId: null,
    };
  }
  const decrypted_account_details = decodeObject(accountDetails, process.env.REACT_APP_LOGIN_SALT);
  const roles = decrypted_account_details?.role;
  const hubs = decrypted_account_details?.hubs;
  const agentRole = Array.isArray(roles) && roles.length > 0 ? (roles[0] ?? null) : null;
  const agentHub = Array.isArray(hubs) && hubs.length > 0 ? (hubs[0] ?? null) : null;

  return {
    agentRole,
    agentEmail: decrypted_account_details?.email,
    agentName: decrypted_account_details?.name,
    agentPhone: decrypted_account_details?.phone,
    agentHub,
    agentAccountId: decrypted_account_details?.id,
    allAgentDetails: decrypted_account_details,
  };
};

export const useBrowserHeight = () => {
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => setHeight(window.innerHeight);

    window.addEventListener("resize", handleResize);

    // Cleanup listener on unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return height;
};

export function useAudioOutputDevices() {
  const [audioOutputs, setAudioOutputs] = useState([]);
  const [selectedOutputId, setSelectedOutputId] = useState(null);
  const [permissionGranted, setPermissionGranted] = useState(null);

  // Request microphone permission
  // const requestAudioPermission = useCallback(async () => {
  //   try {
  //     // Request mic permission
  //     const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

  //     // Stop all audio tracks immediately → mic will NOT be used
  //     stream.getTracks().forEach((track) => track.stop());

  //     setPermissionGranted(true);
  //   } catch (err) {
  //     console.warn("Microphone permission denied:", err);
  //     setPermissionGranted(false);
  //   }
  // }, []);
  const requestAudioPermission = useCallback(async () => {
    try {
      // Check current permission state (if supported by the browser)
      if ("permissions" in navigator) {
        const permissionStatus = await navigator.permissions.query({ name: "microphone" });

        if (permissionStatus.state === "granted") {
          // Already granted, no need to request
          setPermissionGranted(true);
          return;
        } else if (permissionStatus.state === "denied") {
          // Denied, can't request
          setPermissionGranted(false);
          return;
        }
        // If 'prompt', proceed to request below
      }

      // Request mic permission (only if not already granted)
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Stop all audio tracks immediately → mic will NOT be used
      stream.getTracks().forEach((track) => track.stop());

      setPermissionGranted(true);
    } catch (err) {
      console.warn("Microphone permission denied or error:", err);
      setPermissionGranted(false);
    }
  }, []);

  // Load audio output devices
  const loadDevices = useCallback(async () => {
    try {
      // Request permission if not granted
      if (!permissionGranted) {
        await requestAudioPermission();
      }

      // Get all devices
      const devices = await navigator.mediaDevices.enumerateDevices();

      // Get audio output devices
      const outputs = devices
        .filter((d) => d.kind === "audiooutput")
        .map((d) => ({
          deviceId: d.deviceId,
          label: d.label || "Unknown Output Device",
        }));

      // ---------------------------
      // REAL HEADPHONE DETECTION
      // ---------------------------

      const headphoneKeywords =
        /headphone|headset|earbud|earbuds|buds|airpods|bluetooth|usb audio|stereo/i;
      const excludeKeywords =
        /default|communications|virtual|vb-audio|cable|loopback|aux|realtek|hdmi|nvidia|speakers|monitor/i;

      const realHeadphones = outputs.filter((d) => {
        const label = d.label.toLowerCase();
        return headphoneKeywords.test(label) && !excludeKeywords.test(label);
      });

      // Update audio outputs in your state
      setAudioOutputs(realHeadphones);

      // Auto-select device:
      // 1st priority → real headphones
      // 2nd priority → first output device
      if (!selectedOutputId) {
        if (realHeadphones.length > 0) {
          setSelectedOutputId(realHeadphones[0].deviceId);
        } else if (outputs.length > 0) {
          setSelectedOutputId(outputs[0].deviceId);
        }
      }
    } catch (err) {
      console.error("Failed to load audio output devices:", err);
    }
  }, [permissionGranted, selectedOutputId, requestAudioPermission]);

  // Listen for plug/unplug
  useEffect(() => {
    loadDevices();

    navigator.mediaDevices.addEventListener("devicechange", loadDevices);

    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", loadDevices);
    };
  }, [loadDevices]);

  const selectOutput = (deviceId) => {
    setSelectedOutputId(deviceId);
  };

  return {
    audioOutputs,
    selectedOutputId,
    selectOutput,
    permissionGranted,
    refresh: loadDevices,
    requestAudioPermission, // Expose this if UI wants to trigger it manually
  };
}
export function useMemoryPressure() {
  const [pressure, setPressure] = useState("normal");

  useEffect(() => {
    const handler = (event) => {
      setPressure(event.type === "memorypressure" ? "critical" : "normal");
    };

    window.addEventListener("memorypressure", handler);

    return () => {
      window.removeEventListener("memorypressure", handler);
    };
  }, []);

  return pressure; // "normal" or "critical"
}

export function useDeviceDetector() {
  const [device, setDevice] = useState("desktop");

  useEffect(() => {
    const detect = () => {
      // Chrome modern detection
      if (navigator.userAgentData) {
        return navigator.userAgentData.mobile ? "mobile" : "desktop";
      }

      // User agent fallback
      const ua = navigator.userAgent.toLowerCase();
      if (/android|iphone|ipad|ipod|windows phone/.test(ua)) {
        return "mobile";
      }

      // Width fallback
      return window.innerWidth <= 768 ? "mobile" : "desktop";
    };

    setDevice(detect());
  }, []);

  return device;
}
export const useHeapMemory = (refreshInterval = 10000) => {
  const [memory, setMemory] = useState({
    usedJSHeapSize: 0,
    totalJSHeapSize: 0,
    jsHeapSizeLimit: 0,
  });

  useEffect(() => {
    // Check if browser supports the API
    const hasMemoryAPI = performance && performance.memory && performance.memory.usedJSHeapSize;

    if (!hasMemoryAPI) {
      console.warn("performance.memory is not supported in this browser.");
      return;
    }

    const interval = setInterval(() => {
      const { usedJSHeapSize, totalJSHeapSize, jsHeapSizeLimit } = performance.memory;

      setMemory({
        usedJSHeapSize,
        totalJSHeapSize,
        jsHeapSizeLimit,
      });
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [refreshInterval]);

  return {
    usedMB: (memory.usedJSHeapSize / 1024 / 1024).toFixed(2),
    totalMB: (memory.totalJSHeapSize / 1024 / 1024).toFixed(2),
    limitMB: (memory.jsHeapSizeLimit / 1024 / 1024).toFixed(2),
    ...memory,
  };
};

export function useCpuMonitor(refreshInterval = 10000) {
  const [cpuLoad, setCpuLoad] = useState(0); // percent
  const [longTasks, setLongTasks] = useState(0);
  const [eventLoopDelay, setEventLoopDelay] = useState(0);

  useEffect(() => {
    let lastTime = performance.now();
    let longTaskCount = 0;

    // Detect long tasks (CPU spikes)
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.duration > 50) {
          longTaskCount++;
        }
      }
    });

    try {
      observer.observe({ entryTypes: ["longtask"] });
    } catch (e) {
      console.warn("LongTask API not supported in this browser.");
    }

    const interval = setInterval(() => {
      const now = performance.now();

      // Event loop delay (detect main thread blocking)
      const delay = now - lastTime - refreshInterval;
      setEventLoopDelay(delay > 0 ? delay : 0);

      // CPU load calculation (approx)
      const estimatedCpu = Math.min(100, Math.max(0, delay * 10));
      setCpuLoad(Math.round(estimatedCpu));

      // Update long tasks count
      setLongTasks(longTaskCount);
      longTaskCount = 0;

      lastTime = now;
    }, refreshInterval);

    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, [refreshInterval]);

  return {
    cpuLoad, // estimated CPU usage %
    eventLoopDelay, // ms
    longTasks, // per second
  };
}

export const useGeoLocationPermission = () => {
  const [location, setLocation] = useState(null); // {lat, long}
  const [status, setStatus] = useState("checking"); // granted / denied / prompt / checking

  const getLocation = async () => {
    if (!navigator.permissions || !navigator.geolocation) {
      setStatus("denied");
      setLocation(null);
      return null;
    }

    try {
      const permission = await navigator.permissions.query({ name: "geolocation" });
      setStatus(permission.state);

      // ---- Permission Granted ----
      if (permission.state === "granted") {
        return await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              const data = {
                lat: pos.coords.latitude,
                long: pos.coords.longitude,
              };
              setLocation(data);
              resolve(data);
            },
            () => {
              setLocation(null);
              resolve(null);
            }
          );
        });
      }

      // ---- Permission Prompt → Show popup ----
      if (permission.state === "prompt") {
        return await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              const data = {
                lat: pos.coords.latitude,
                long: pos.coords.longitude,
              };
              setLocation(data);
              setStatus("granted");
              resolve(data);
            },
            () => {
              setLocation(null);
              setStatus("denied");
              resolve(null);
            }
          );
        });
      }

      // ---- Permission Denied ----
      if (permission.state === "denied") {
        setLocation(null);
        return null;
      }

      return null;
    } catch (err) {
      setLocation(null);
      setStatus("denied");
      return null;
    }
  };

  return {
    status, // "granted" | "prompt" | "denied" | "checking"
    location, // {lat, long} or null
    getLocation, // function to manually re-check permission & get location
  };
};

const getDeviceType = (width) => {
  if (width <= 767) return "mobile";
  if (width <= 1024) return "tablet";
  return "desktop";
};

const getOrientation = () =>
  window.matchMedia("(orientation: portrait)").matches ? "portrait" : "landscape";

const getInitialState = () => {
  if (typeof window === "undefined") {
    return {
      deviceType: "unknown",
      width: 0,
      height: 0,
      orientation: "unknown",
      isMobile: false,
      isTablet: false,
      isDesktop: false,
      pixelRatio: 1,
      touchSupported: false,
    };
  }

  const width = window.innerWidth;
  const height = window.innerHeight;
  const deviceType = getDeviceType(width);

  return {
    deviceType,
    width,
    height,
    orientation: getOrientation(),
    isMobile: deviceType === "mobile",
    isTablet: deviceType === "tablet",
    isDesktop: deviceType === "desktop",
    pixelRatio: window.devicePixelRatio || 1,
    touchSupported: "ontouchstart" in window || navigator.maxTouchPoints > 0,
  };
};

export const useDeviceInfo = () => {
  const [device, setDevice] = useState(getInitialState);

  useEffect(() => {
    const updateDeviceInfo = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const deviceType = getDeviceType(width);

      setDevice({
        deviceType,
        width,
        height,
        orientation: getOrientation(),
        isMobile: deviceType === "mobile",
        isTablet: deviceType === "tablet",
        isDesktop: deviceType === "desktop",
        pixelRatio: window.devicePixelRatio || 1,
        touchSupported: "ontouchstart" in window || navigator.maxTouchPoints > 0,
      });
    };

    updateDeviceInfo();

    window.addEventListener("resize", updateDeviceInfo);
    window.addEventListener("orientationchange", updateDeviceInfo);

    return () => {
      window.removeEventListener("resize", updateDeviceInfo);
      window.removeEventListener("orientationchange", updateDeviceInfo);
    };
  }, []);

  return device;
};
